import { ComponentPropsWithoutRef, forwardRef, createContext } from "react";
import styles from "./Listbox.module.scss";
import classNames from "classnames";
import { Text } from "../Text";

/**
 * Props for the ListboxOptionGroup component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ListboxOptionGroupProps = ComponentPropsWithoutRef<"div"> & {
  /** Whether the group is disabled */
  disabled?: boolean;
  /** The label for the option group */
  label: string;
};

type ListboxOptionGroupContextProps = {
  disabled?: boolean;
};

export const ListboxOptionGroupContext =
  createContext<ListboxOptionGroupContextProps>({});

/**
 * ListboxOptionGroup component for grouping related options within a listbox.
 *
 * Features:
 * - Groups related options with a descriptive label
 * - Maintains proper ARIA structure for accessibility
 * - Supports keyboard navigation within groups
 * - Visual separation of option categories
 *
 * @example
 * <ListboxOptionGroup label="Fruits">
 *   <ListboxOption value="apple">Apple</ListboxOption>
 *   <ListboxOption value="banana">Banana</ListboxOption>
 * </ListboxOptionGroup>
 */
export const ListboxOptionGroup = forwardRef<
  HTMLDivElement,
  ListboxOptionGroupProps
>((props, ref) => {
  const { className, children, disabled, label, ...rest } = props;

  const optionGroupCx = classNames(styles["option-group"], className);

  const labelCx = classNames(styles["label"], {
    [styles["disabled"]]: disabled,
  });

  return (
    <ListboxOptionGroupContext.Provider value={{ disabled }}>
      <div
        data-anv="listbox-option-group"
        className={optionGroupCx}
        ref={ref}
        {...rest}
      >
        <Text variant="eyebrow" className={labelCx}>
          {label}
        </Text>

        {children}
      </div>
    </ListboxOptionGroupContext.Provider>
  );
});

ListboxOptionGroup.displayName = "ListboxOptionGroup";
